import {
  SALES_PLANNER_USER_SETTINGS_DEFAULTS,
  SalesPlannerUserSettings,
} from '@quorum/models/xs-resource';
import { LocalSettingsState } from './local-settings.interfaces';

// Re-export the shared type so existing imports of `PersistedSalesPlannerSettings`
// from this util keep working without forcing every caller through the models barrel.
export type PersistedSalesPlannerSettings = SalesPlannerUserSettings;

export const PERSISTED_SALES_PLANNER_DEFAULTS: SalesPlannerUserSettings = SALES_PLANNER_USER_SETTINGS_DEFAULTS;

// Returns true when the persisted-subset projection of `prev` and `next` is byte-identical, i.e.
// the user's dialog edits only touched session-only fields (activitiesFilter, prospectFilter,
// prospectSortBy, startDate). Used by the facade to skip a no-op PUT + misleading success toast.
export function persistedSubsetUnchanged(prev: LocalSettingsState, next: LocalSettingsState): boolean {
  return JSON.stringify(pickPersistedSettings(prev)) === JSON.stringify(pickPersistedSettings(next));
}

export function pickPersistedSettings(state: LocalSettingsState): SalesPlannerUserSettings {
  return {
    salesPeople: state.salesPeople,
    range: state.range,
    rangePast: state.rangePast,
    monthsOverdue: state.monthsOverdue,
    showAllServiceAppointments: state.showAllServiceAppointments,
    defaultActivityTab: state.defaultActivityTab,
    defaultProspectTab: state.defaultProspectTab,
    activityListDisplayEmployee: state.activityListDisplayEmployee,
    accessibility: {
      theme: state.accessibility.theme,
      boldText: state.accessibility.boldText,
      largeText: state.accessibility.largeText,
      increaseContrast: state.accessibility.increaseContrast,
    },
  };
}

export function mergePersistedIntoState(
  current: LocalSettingsState,
  persisted: Partial<SalesPlannerUserSettings>
): LocalSettingsState {
  return {
    ...current,
    ...persisted,
    accessibility: {
      ...current.accessibility,
      ...(persisted.accessibility || {}),
    },
  };
}

const isPlainObject = (val: unknown): val is Record<string, any> =>
  val !== null && typeof val === 'object' && !Array.isArray(val);

// "current wins" deep merge — fills in only the keys (including nested) that are missing from
// `current`. Used when the server has no stored record but local state may have been hydrated from
// a legacy `salesplannerLocalSettings` value that pre-dates newly-added schema fields. Without
// this, those users would never see new nested defaults (e.g. a future accessibility subkey) until
// they manually re-save.
function deepFillMissing(target: Record<string, any>, defaults: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = { ...target };
  for (const key of Object.keys(defaults)) {
    const fallback = defaults[key];
    const existing = result[key];
    if (existing == null) {
      result[key] = fallback;
    } else if (isPlainObject(existing) && isPlainObject(fallback)) {
      result[key] = deepFillMissing(existing, fallback);
    }
  }
  return result;
}

export function fillDefaultsIntoState(
  current: LocalSettingsState,
  defaults: SalesPlannerUserSettings = SALES_PLANNER_USER_SETTINGS_DEFAULTS
): LocalSettingsState {
  return deepFillMissing(current, defaults) as LocalSettingsState;
}

const sortJoin = (value: any): string => {
  if (Array.isArray(value)) {
    return value.slice().sort().join(',');
  }
  return value == null ? '' : String(value);
};

// Returns true when `a` and `b` differ in any field that the navigation effect reads when issuing
// the initial activity / prospect data fetches (see salesplanner-legacy.effects.ts:99-280). When
// they are equivalent we can skip the post-login refresh dispatch entirely — the data the user
// already sees was loaded with the right filters.
export function loadAffectingSettingsDiffer(a: LocalSettingsState, b: LocalSettingsState): boolean {
  if (a === b) return false;
  if (sortJoin(a.salesPeople) !== sortJoin(b.salesPeople)) return true;
  if (a.range !== b.range) return true;
  if (a.rangePast !== b.rangePast) return true;
  if (a.showAllServiceAppointments !== b.showAllServiceAppointments) return true;
  if (sortJoin(a.activitiesFilter) !== sortJoin(b.activitiesFilter)) return true;

  const af = a.prospectFilter || ({} as any);
  const bf = b.prospectFilter || ({} as any);
  if (sortJoin(af.status) !== sortJoin(bf.status)) return true;
  if (sortJoin(af.typeId) !== sortJoin(bf.typeId)) return true;
  if (sortJoin(af.channel) !== sortJoin(bf.channel)) return true;
  if (sortJoin(af.source) !== sortJoin(bf.source)) return true;
  if ((af.sort || '') !== (bf.sort || '')) return true;

  return false;
}
